import { jwtDecode } from 'jwt-decode';

const STORAGE_KEYS = ['token', 'userid', 'name', 'rolle', 'role', 'doctorId', 'tenantId', 'isSuperUser', 'permissions', 'permissionScopes'];

export function clearAuthStorage() {
  STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

export function getAuthToken() {
  return localStorage.getItem('token') || '';
}

export function getStoredRole() {
  return (localStorage.getItem('role') || localStorage.getItem('rolle') || '').trim();
}

export function normalizeRole(role) {
  return String(role || '').trim().toLowerCase();
}

export function getAuthSession() {
  const token = getAuthToken();
  const role = localStorage.getItem('role') || localStorage.getItem('rolle') || '';
  const tenantId = localStorage.getItem('tenantId') || '';
  const doctorId = localStorage.getItem('doctorId') || '';
  const name = localStorage.getItem('name') || '';
  const isSuperUser = localStorage.getItem('isSuperUser') === 'true';
  const permissions = parseStoredJson(localStorage.getItem('permissions'), []);
  const permissionScopes = parseStoredJson(localStorage.getItem('permissionScopes'), {});

  return {
    token,
    role,
    tenantId,
    doctorId,
    name,
    permissions,
    permissionScopes,
    isSuperUser,
    isGlobalAdmin: isSuperUser,
    isAuthenticated: Boolean(token),
  };
}

function parseStoredJson(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
}

export function hasPermission(permissionKey, session = getAuthSession()) {
  if (!permissionKey) return true;
  if (session?.isSuperUser) return true;
  return Array.isArray(session?.permissions) && session.permissions.includes(permissionKey);
}

export function hasAnyPermission(permissionKeys = [], session = getAuthSession()) {
  const keys = Array.isArray(permissionKeys) ? permissionKeys.filter(Boolean) : [permissionKeys].filter(Boolean);
  if (!keys.length) return true;
  if (session?.isSuperUser) return true;
  return keys.some((permissionKey) => hasPermission(permissionKey, session));
}

export function storeAuthSession(session) {
  if (session.name !== undefined) localStorage.setItem('name', session.name || '');
  if (session.rolle !== undefined) localStorage.setItem('rolle', session.rolle || '');
  if (session.role !== undefined) localStorage.setItem('role', session.role || '');
  if (session.doctorId !== undefined) localStorage.setItem('doctorId', String(session.doctorId ?? ''));
  if (session.tenantId !== undefined) localStorage.setItem('tenantId', String(session.tenantId ?? ''));
  if (session.token !== undefined) localStorage.setItem('token', session.token || '');
  if (session.userid !== undefined) localStorage.setItem('userid', JSON.stringify(session.userid));
  if (session.isSuperUser !== undefined) localStorage.setItem('isSuperUser', String(Boolean(session.isSuperUser)));
  if (session.permissions !== undefined) localStorage.setItem('permissions', JSON.stringify(session.permissions || []));
  if (session.permissionScopes !== undefined) localStorage.setItem('permissionScopes', JSON.stringify(session.permissionScopes || {}));
}

export function isTokenExpired(token) {
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    if (!exp) return true;
    return exp < Date.now() / 1000;
  } catch (error) {
    return true;
  }
}

export function hasRequiredRole(role, allowedRoles = [], options = {}) {
  if (options.requireGlobalAdmin) {
    return Boolean(options.isSuperUser);
  }

  if (!allowedRoles.length) {
    return true;
  }

  if (options.isSuperUser) {
    return true;
  }

  return allowedRoles.map(normalizeRole).includes(normalizeRole(role));
}

